import React from 'react';
import { Box, Typography, Button, Container, useTheme, useMediaQuery, Paper, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { ArrowForward as ArrowForwardIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box
      sx={{
        position: 'relative',
        backgroundImage: 'linear-gradient(135deg, #FAF8F5 0%, #FBF9F6 100%)',
        color: theme.palette.text.primary,
        py: { xs: 10, md: 14 },
        mb: 8,
        overflow: 'hidden',
        borderRadius: { xs: 0, md: '0 0 30px 30px' },
        boxShadow: '0 10px 40px rgba(45, 41, 38, 0.15)',
      }}
    >
      {/* Animated background patterns */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23F1B434\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E")',
          animation: 'panBackground 60s linear infinite',
          '@keyframes panBackground': {
            '0%': { backgroundPosition: '0% 0%' },
            '100%': { backgroundPosition: '100% 100%' },
          },
        }}
      />
      
      {/* Animated gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'radial-gradient(circle at 30% 30%, rgba(241, 180, 52, 0.3) 0%, rgba(0, 0, 0, 0) 70%)',
        }}
      />
      
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ position: 'relative' }}>
              <Typography 
                variant="h1" 
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  mb: 3,
                  background: 'linear-gradient(90deg, #2D2926 0%, #F1B434 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: 0,
                    width: '80px',
                    height: '4px',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '4px',
                  },
                }}
              >
                The Reckoning Project
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 3,
                  color: theme.palette.primary.main,
                  maxWidth: '600px',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  lineHeight: 1.4,
                }}
              >
                Promoting transparency and justice for those harmed by misconduct in the U.S. criminal legal system.
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 400, 
                  mb: 5,
                  color: 'rgba(45, 41, 38, 0.8)',
                  maxWidth: '600px',
                  fontSize: { xs: '1.0rem', md: '1.1rem' },
                  lineHeight: 1.5,
                }}
              >
                We're building a comprehensive database of official misconduct to create transparency, accountability, and incentives for appropriate behavior among legal actors.
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                gap: 2,
                position: 'relative',
                zIndex: 5
              }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  component={Link}
                  to="/submit-case"
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1.05rem',
                    boxShadow: '0 6px 20px rgba(241, 180, 52, 0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.6s',
                    },
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(241, 180, 52, 0.4)',
                      '&::before': {
                        transform: 'translateX(100%)',
                      },
                    },
                  }}
                >
                  Request Questionnaire
                </Button>
                
                <Button 
                  variant="outlined" 
                  size="large"
                  component={Link}
                  to="/about"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px',
                    fontWeight: 600,
                    fontSize: '1.05rem',
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      borderWidth: '2px',
                      backgroundColor: 'rgba(241, 180, 52, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        {/* Scroll indicator */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 8,
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-20px)' },
              '60%': { transform: 'translateY(-10px)' },
            },
          }}
        >
          <KeyboardArrowDownIcon 
            sx={{ 
              fontSize: 40, 
              color: '#888B8D',
              cursor: 'pointer',
            }} 
            onClick={() => window.scrollTo({
              top: window.innerHeight - 100,
              behavior: 'smooth'
            })}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
